import { Button, Checkbox, Form, Input, List, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { useEffect, useState } from 'react'
import { MapServerLayer, useGetProjectMapServerLayersQuery } from 'schema'
import { alphabeticalSort } from 'shared'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { ProjectLayersProps } from './types'

const ProjectLayers: React.FC<ProjectLayersProps> = ({
  initialLayers,
  onSave,
  loading: _loading,
  onChange,
}) => {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const [form] = useForm()
  useEffect(() => () => form.resetFields(), [form])
  const { data: mapServerLayersData, loading: layerLoading } =
    useGetProjectMapServerLayersQuery({
      ...DefaultQueryHookOptions,
      skip: !projectGlobalId,
      variables: { globalId: projectGlobalId! },
    })
  const projectFeatureLayers =
    mapServerLayersData?.project?.projectMetadata?.mapServerLayers
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(_loading || layerLoading)
    return () => setLoading(false)
  }, [_loading, layerLoading])
  const [search, setSearch] = useState<string>()
  const [checked, setChecked] = useState<Set<number>>(new Set())
  const [children, setChildren] = useState<MapServerLayer[]>([])
  useEffect(() => {
    if (!projectFeatureLayers?.length) return
    let layers = [...projectFeatureLayers]
    if (!!search)
      layers = layers.filter(({ name }) =>
        name.toLowerCase().includes(search.toLowerCase())
      )
    setChildren(layers.sort(alphabeticalSort('name')))
    return () => setChildren([])
  }, [projectFeatureLayers, search])

  useEffect(() => {
    if (!projectFeatureLayers?.length) return
    form.resetFields()
    form.setFields(
      projectFeatureLayers.map(({ id }) => ({
        name: id,
        value: initialLayers?.includes(id) ? true : false,
      }))
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLayers, projectFeatureLayers])

  const _onChange = async (preChange?: () => void) => {
    preChange?.()
    // form.validateFields will only validate _visible_ fields
    const layers = await form.validateFields()
    // iterate the layer results and add them to a Set to deal with potential dups
    // from multiple on change events intermingled with search events which limit
    // visibility
    Object.keys(layers)
      .map((layer) => ({
        layer,
        checked: !!layers[layer],
      }))
      .forEach(({ checked, layer }) =>
        setChecked((prev) => {
          if (checked) prev.add(+layer)
          else prev.delete(+layer)
          return prev
        })
      )
    onChange([...checked])
  }

  return (
    <Form onChange={() => _onChange()} form={form}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space size="middle" style={{ width: '100%' }}>
          <Button
            onClick={() =>
              _onChange(() =>
                form.setFields(
                  children.map(({ id }) => ({ name: id, value: true }))
                )
              )
            }
          >
            Select All
          </Button>
          <Button onClick={() => _onChange(() => form.resetFields())}>
            Select None
          </Button>
          <Button type="primary" onClick={onSave}>
            Save
          </Button>
        </Space>
        <Input.Search
          onSearch={(value) => setSearch(value)}
          allowClear
          enterButton
          placeholder="Search"
        />
        <List
          style={{
            maxHeight: '50vh',
            width: '100%',
            overflowY: !!children?.length ? 'scroll' : undefined,
          }}
          size="small"
          split={false}
          loading={loading}
          dataSource={children}
          renderItem={({ id, name }) => (
            <List.Item>
              <Form.Item name={id} valuePropName="checked" noStyle>
                <Checkbox>{name}</Checkbox>
              </Form.Item>
            </List.Item>
          )}
        />
      </Space>
    </Form>
  )
}

export default ProjectLayers
